import { TextLibraryEntry, TextLibraryPayload } from '../types';

// generous bounds (a hymnal has about a thousand hymns) so a faulty host payload cannot freeze the editor:
// the library text is inserted untruncated
export const MAX_TEXT_LIBRARY_ENTRIES = 5000;
export const MAX_TEXT_LIBRARY_VERSES = 100;
export const MAX_TEXT_LIBRARY_TEXT_LENGTH = 5000;

function toTrimmedString(value: unknown): string {
  if (typeof value === 'string') {
    return value.slice(0, MAX_TEXT_LIBRARY_TEXT_LENGTH).trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
}

function sanitizeEntry(raw: unknown): TextLibraryEntry | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const entry = raw as Record<string, unknown>;
  const id = toTrimmedString(entry.id);
  const title = toTrimmedString(entry.title);
  const verses = Array.isArray(entry.verses)
    ? entry.verses
        .map((verse) => toTrimmedString(verse).replace(/\r\n?/g, '\n'))
        .filter(Boolean)
        .slice(0, MAX_TEXT_LIBRARY_VERSES)
    : [];
  if (!id || !title || !verses.length) {
    return undefined;
  }

  const result: TextLibraryEntry = { id, title, verses };
  const number = toTrimmedString(entry.number);
  const credits = toTrimmedString(entry.credits);
  const language = toTrimmedString(entry.language);
  const collection = toTrimmedString(entry.collection);
  if (number) {
    result.number = number;
  }
  if (credits) {
    result.credits = credits;
  }
  if (language) {
    result.language = language;
  }
  if (collection) {
    result.collection = collection;
  }
  return result;
}

/**
 * Host data is untrusted: drop entries without id, title or any verse, and duplicate ids,
 * so the UI never has to guard against malformed input. Entries, verses and texts are capped.
 */
function sanitizeTextLibraryPayload(payload: unknown): TextLibraryPayload {
  const data = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>;
  const seenIds = new Set<string>();
  const entries: TextLibraryEntry[] = [];
  const rawEntries: unknown[] = Array.isArray(data.entries) ? data.entries : [];
  for (let i = 0; i < rawEntries.length && entries.length < MAX_TEXT_LIBRARY_ENTRIES; i += 1) {
    const entry = sanitizeEntry(rawEntries[i]);
    if (entry && !seenIds.has(entry.id)) {
      seenIds.add(entry.id);
      entries.push(entry);
    }
  }
  const title = toTrimmedString(data.title);
  const result: TextLibraryPayload = { title: title || undefined, entries };
  if (data.includeTitle === false) {
    result.includeTitle = false;
  }
  return result;
}

export default sanitizeTextLibraryPayload;
